import { Factory } from '../../services/Factory';
import { BasicSearch } from '../../model/search/basic/BasicSearch';
import { AdvancedSearch } from '../../model/search/advanced/AdvancedSearch';
import { SearchContext } from '../../model/search/ISearch';

namespace app.controllers.search {

  interface ISearchScope extends any {
    project: any;
    collection: any;
    results: any;
    pagination: {
      limit: number
    },
    searchContext: any;
    generateResourceReport: Function;
    generateTagSearchReport: Function;
    createBatches: Function;
    search: any;
    searchService: any;
    cookieFilter: any;
    display: { mode: any };
    DisplayMode: any;
    gridViewDataSource: agGrid.IDatasource;
    listViewDataSource: agGrid.IDatasource;
    gridViewColumnDefinitions: any[];
    listViewColumnDefinitions: any[];
    totalResources: number;
    totalPageCount: number;
    totalFileSize: number;
    sortModel: any;
    filterModel: any;
    ListViewCellRenderer: any;
    Status;
    selectAll: { state: boolean };
  }

  export class SearchController {
    private deferredCursorCallbacks: any[] = [];

    constructor(private rootScope: IRootScope, private scope: ISearchScope, cursorService: any, projectService: app.services.project.ProjectService, collectionService: app.services.collections.CollectionService, layerService: any, basicSearchService: app.services.search.basic.SearchService, advancedSearchService: app.services.search.advanced.SearchService, modal: any, private preferenceService: PreferenceService, resourceService: ResourceService) {
      scope.sortModel = preferenceService.Get(Preferences.GRID_COLUMNS_SORT_MODEL);
      scope.filterModel = preferenceService.Get(Preferences.GRID_COLUMNS_FILTER_MODEL);
      scope.cursor = cursorService;
      scope.project = projectService.GetCurrentProject();
      scope.collection = collectionService.GetCurrentCollection();
      scope.results = {};
      scope.search = new BasicSearch(scope.searchContext, "");
      scope.DisplayMode = DisplayMode;
      scope.Status = Status;
      scope.ListViewCellRenderer = ResourceCellRenderer;
      scope.display = {mode: preferenceService.Get(Preferences.DISPLAY_MODE) ? preferenceService.Get(Preferences.DISPLAY_MODE) : DisplayMode.ListView};
      scope.pagination = {
        limit: scope.display.mode === DisplayMode.ListView ? 20 : 200
      };
      scope.selectAll = {state: false};

      scope.$on("ProjectService.CurrentProjectChanged", (e, project) => scope.project = project);

      scope.$on("CollectionService.CurrentCollectionChanged", (e, collection) => scope.collection = collection);

      scope.$on("SearchContext.Changed", (e, context) => {
        scope.searchContext = context;
        scope.collection = context === SearchContext.Collection ? collectionService.GetCurrentCollection() : null;
      });

      scope.$on("Search.SearchExecuted", (e, args) => {
        if (args.advanced) {
          scope.search = new AdvancedSearch(scope.searchContext, args.expression);
          scope.searchService = advancedSearchService;
        } else {
          scope.search = new BasicSearch(scope.searchContext, args.expression);
          scope.searchService = basicSearchService;
        }

        rootScope.query = () => this.scope.search['expression'];

        this.InitializeGridSearch();

        scope.display = {mode: preferenceService.Get(Preferences.DISPLAY_MODE) ? preferenceService.Get(Preferences.DISPLAY_MODE) : DisplayMode.ListView};
        layerService.pushLayer(layerService.LAYERS.SEARCH);
      });

      scope.$on("DataGridRowSelectedEvent", (e, params) => {
        let {projectId, collectionId, id} = params.data;

        resourceService.LaunchFullView({
          projectId,
          collectionId,
          resourceId: id,
          grid: params.grid,
          index: params.index
        });
      });

      scope.$on("DataGridSortChangedEvent", (e, sort) => {
        preferenceService.Set(Preferences.GRID_COLUMNS_SORT_MODEL, sort);
      });

      scope.$on("DataGridFilterChangedEvent", (e, filter) => {
        preferenceService.Set(Preferences.GRID_COLUMNS_FILTER_MODEL, filter);
        scope.columnFilters = DataGrid.GetSearchExpressionFromFilterModel(filter);
      });

      scope.$on("DataGridColumnsChangedEvent", (e, columns) => {
        preferenceService.Set(Preferences.GRID_COLUMNS, columns);
      });

      scope.$on("DataGridRowCheckedEvent", (e, params) => {
        scope.resources = _.map(params.grid.api.getSelectedRows(), (r: IResource) => r.id);
      });

      scope.$on("DataGridColumnsResetEvent", () => {
        preferenceService.ResetGridPreferences();
        this.InitializeGridSearch();
      });

      scope.$watch("selectAll", (value: { state: boolean }) => {
        if (value.state) {
          scope.resources = [];
        }
      }, true);

      scope.generateResourceReport = () => {
        let currentDate = new Date();

        modal.open({
          backdrop: true,
          keyboard: true,
          backdropClick: true,
          templateUrl: 'partials/reporting/dialog_report.html',
          controller: 'AddReportModalCtrl',
          scope: scope,
          resolve: {
            project: () => scope.project,
            config: () => {
              return {
                type: "resource_report_search",
                format: "csv",
                name: `Search Report ${(currentDate.getMonth() + 1)}/${currentDate.getDate()}/${currentDate.getFullYear()} @ ${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`
              };
            },
            formConfig: () => {
            },
            collections: () => scope.collection ? [scope.collection.id] : [],
            search: () => scope.search,
            filter: () => scope.cookieFilter,
            resources: () => scope.selectAll.state ? undefined : scope.resources,
            selectAll: () => scope.selectAll.state
          }
        });
      };

      scope.generateTagSearchReport = () => {
        let currentDate = new Date();

        modal.open({
          backdrop: true,
          keyboard: true,
          backdropClick: true,
          templateUrl: 'partials/dialog_tag_report.html',
          controller: 'AddTagReportModalCtrl',
          scope: scope,
          resolve: {
            project: () => scope.project,
            config: () => {
              return {
                type: "tag_search_report",
                name: `Tag Report ${(currentDate.getMonth() + 1)}/${currentDate.getDate()}/${currentDate.getFullYear()} @ ${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`
              };
            },
            formConfig: () => {
            },
            collections: () => scope.collection ? [scope.collection.id] : [],
            search: () => scope.search,
            filter: scope.cookieFilter,
            resources: () => scope.selectAll.state ? undefined : scope.resources,
            selectAll: () => scope.selectAll.state
          }
        });
      };

      scope.createBatches = () => {
        modal.open({
          backdrop: true,
          keyboard: true,
          backdropClick: true,
          templateUrl: 'partials/batching/dialog_batch.html',
          controller: 'AddBatchModalController',
          scope: scope,
          resolve: {
            project: () => scope.project,
            collection: () => scope.collection ? [{id: scope.collection.id}] : [],
            search: () => scope.search,
            columnFilters: () => new AdvancedSearch(scope.searchContext, scope.columnFilters),
            filter: () => scope.cookieFilter,
            resources: () => scope.selectAll.state ? undefined : scope.resources,
            selectAll: () => scope.selectAll.state
          }
        });
      };

      scope.$watch("display.mode", (value: DisplayMode, prev: DisplayMode) => {
        if (value !== prev) {
          preferenceService.Set(Preferences.DISPLAY_MODE, value);
          this.InitializeGridSearch();
        }
      });
    }

    private InitializeGridSearch() {
      const self = this;
      const dataSource = {
        rowCount: null,
        getRows(params: agGrid.IGetRowsParams) {
          if (self.scope.searchService) {
            self.scope.searchService.Search(self.scope.project, self.scope.collection, self.scope.search, self.scope.columnFilters, params.sortModel.length > 0 ? params.sortModel[0].colId : null, params.sortModel.length > 0 ? params.sortModel[0].sort.toUpperCase() : null, Math.floor(params.startRow / self.scope.pagination.limit) + 1, self.scope.pagination.limit).then((results: any) => {
              let resources = _.map(results.hits.hits, (hit: any) => {
                return {
                  id: hit.fields.resourceId,
                  projectId: hit.fields.projectId,
                  collectionId: hit.fields.collectionId,
                  title: hit.fields.title,
                  emailFrom: hit.fields.emailFrom,
                  emailTo: hit.fields.emailTo,
                  emailCc: hit.fields.emailCc,
                  emailBcc: hit.fields.emailBcc,
                  creator: hit.fields.creator,
                  filename: hit.fields.fileName,
                  category: hit.fields.category,
                  type: hit.fields.type,
                  emailConversationTopic: hit.fields.emailConversationTopic,
                  path: hit.fields.path,
                  directPdfThumbnailPath: hit.fields.directPdfThumbnailPath,
                  folders: hit.fields.folders,
                  numPdfPages: hit.fields.pageCount,
                  bytes: hit.fields.fileSize,
                  extension: hit.fields.fileExtension,
                  custodian: hit.fields.custodian,
                  createdDate: hit.fields.createdDate,
                  modifiedDate: hit.fields.modifiedDate,
                  mimeType: hit.fields.mimeType,
                  md5: hit.fields.md5,
                  lastBegBates: hit.fields.lastBegBates,
                  lastEndBates: hit.fields.lastEndBates,
                  lastBegAttachBates: hit.fields.lastBegAttachBates,
                  lastEndAttachBates: hit.fields.lastEndAttachBates,
                  isContainer: hit.fields.isContainer,
                  isSourceStorage: hit.fields.isSourceStorage,
                  isTopParent: hit.fields.isTopParentResource,
                  score: (hit._score / results.hits.max_score) * 100,
                  hasNotes: hit.fields.hasNotes,
                  hadProcessingException: hit.fields.hadProcessingException,
                  hasVirus: hit.fields.hasVirus,
                  hasPrivilegedNotes: hit.fields.hasPrivilegedNotes,
                  chatConversationId: hit.fields.chatConversationId,
                  chatMessageId: hit.fields.chatMessageId,
                  chatMessage: hit.fields.chatMessage,
                  sortKey: hit.fields.sortKey
                };
              });

              self.scope.totalResources = results.hits.total;
              self.scope.totalPageCount = results.totalPageCount;
              self.scope.totalFileSize = results.totalFileSize;

              params.successCallback(resources, results.hits.total);
            }, () => {
              self.scope.totalResources = 0;
              self.scope.totalPageCount = 0;
              self.scope.totalFileSize = 0;
            });
          }
        }
      };

      this.scope.sortModel = this.preferenceService.Get(Preferences.GRID_COLUMNS_SORT_MODEL);
      this.scope.filterModel = this.preferenceService.Get(Preferences.GRID_COLUMNS_FILTER_MODEL);
      this.scope.columnFilters = DataGrid.GetSearchExpressionFromFilterModel(this.scope.filterModel);

      if (this.preferenceService.Get(Preferences.DISPLAY_MODE) == DisplayMode.GridView) {
        this.scope.gridViewColumnDefinitions = this.preferenceService.Get(Preferences.GRID_COLUMNS);
        this.scope.gridViewDataSource = dataSource;
      } else {
        this.scope.listViewColumnDefinitions = this.preferenceService.Get(Preferences.GRID_COLUMNS);
        this.scope.listViewDataSource = dataSource;
      }
    }
  }

  frontendApp.controller("SearchController", ["$rootScope", "$scope", "CursorService", "ProjectService", "CollectionService", "LayerService", "BasicSearchService", "AdvancedSearchService", "$uibModal", "PreferenceService", "ResourceService", Factory.Create(SearchController)]);
}
